import { twClassNames } from "@scripts/react/helpers/twClassNames";
import React from "react";

interface ActionCardProps {
    title: string;
    description?: string;
    icon?: string;
    iconComponent?: React.ReactNode;
    children?: React.ReactNode;
    type?: "primary" | "secondary";
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
    size?: "sm" | "md" | "lg";
    expanded?: boolean;
    expandedContent?: React.ReactNode;
}

const SIZE_CONFIG = {
    sm: {
        title: "text-base",
        primaryIcon: "text-base",
        primaryContainer: "h-8 w-8",
        nonPrimaryIcon: "text-base"
    },
    md: {
        title: "text-base",
        primaryIcon: "text-base",
        primaryContainer: "h-12 w-12",
        nonPrimaryIcon: "text-base"
    },
    lg: {
        title: "text-base",
        primaryIcon: "text-base",
        primaryContainer: "h-12 w-12",
        nonPrimaryIcon: "text-base"
    }
} as const;

export const ActionCard = ({
    title,
    description,
    icon,
    iconComponent,
    onClick,
    children,
    type = "secondary",
    disabled,
    className,
    size = "md",
    expanded = false,
    expandedContent
}: ActionCardProps) => {
    const config = SIZE_CONFIG[size];
    const isPrimary = type === "primary";

    const getIconClass = (iconString: string) => {
        const prefix = isPrimary ? "far" : "fal";
        return iconString.replace(/^far|^fal/, prefix);
    };

    const getContainerClasses = () => {
        const baseClasses =
            "flex flex-col rounded-lg border transition-all duration-300 overflow-hidden";

        if (isPrimary) {
            const primaryClasses = disabled
                ? "border-brand-disabled cursor-not-allowed"
                : expanded
                  ? "border-brand "
                  : "border-brand hover:border-brand-hover hover:bg-tint cursor-pointer";
            return twClassNames(baseClasses, primaryClasses, className);
        }

        const secondaryClasses = disabled
            ? "border-gray-300 cursor-not-allowed"
            : expanded
              ? "border-gray-300"
              : "border-gray-300 hover:border-gray-500 hover:bg-gray-100 cursor-pointer";
        return twClassNames(baseClasses, secondaryClasses, className);
    };

    const getTitleClasses = () => {
        const baseClasses = twClassNames(config.title, "font-medium");

        if (isPrimary) {
            return disabled
                ? twClassNames(baseClasses, "text-brand-disabled")
                : twClassNames(baseClasses, "text-brand");
        }

        return twClassNames(baseClasses, disabled ? "text-gray-300" : "text-secondary");
    };

    const renderIcon = () => {
        if (!icon) return iconComponent;

        if (isPrimary) {
            const containerClasses = twClassNames(
                "flex items-center justify-center rounded-lg flex-shrink-0",
                config.primaryContainer,
                config.primaryIcon,
                disabled ? "bg-tint text-brand-disabled" : "bg-tint text-brand"
            );

            return (
                <div className={containerClasses}>
                    <i className={icon}></i>
                </div>
            );
        }

        const iconClasses = twClassNames(
            getIconClass(icon),
            config.nonPrimaryIcon,
            disabled ? "text-gray-300" : "text-secondary"
        );
        return <i className={iconClasses}></i>;
    };

    return (
        <div className={getContainerClasses()} onClick={disabled || expanded ? undefined : onClick}>
            <div className="flex flex-row items-center gap-4 px-5 py-3">
                {renderIcon()}
                <div className="flex flex-1 flex-col gap-1">
                    <h3 className={getTitleClasses()}>{title}</h3>
                    {description && (
                        <p className="text-sm font-normal text-tertiary">{description}</p>
                    )}
                    {children}
                </div>
            </div>
            {expanded && expandedContent && (
                <div className="border-t border-gray-300 bg-gray-50">{expandedContent}</div>
            )}
        </div>
    );
};
